import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Card, Flex, Modal, Skeleton, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import get from 'lodash-es/get';
import { useContext, useEffect, useMemo, useState } from 'react';

import {
  GlobalSpecialsModalContext,
  SpecialsModalContext,
} from '@jane/business-admin/providers';
import type { SpecialRulesV2 } from '@jane/business-admin/types';
import { BODY_LINE_HEIGHT, MODAL_CARD_WIDTH } from '@jane/business-admin/util';
/* eslint-disable-next-line no-restricted-imports */
import type { ProductThresholdConditions } from '@jane/shared/models';
import { RULE_OPTIONS } from '@jane/shared/util';

import {
  ConditionType,
  FORM_FIELD_DEFAULT_MARGIN,
  getApplyToSelection,
} from '../form';
import { PriceConditions } from './PriceConditions';
import { ProductsConditions } from './ProductsConditions';
import { SpecialTypeConditionsCriteria } from './SpecialTypeConditionsCriteria';
import { WeightAndPriceConditions } from './WeightAndPriceConditions';
import { BrandConditions } from './brandConditions/BrandConditions';
import { CategoryConditions } from './categoryConditions/CategoryConditions';
import { convertRulesToString } from './convertRulesToString';
import { ConditionByCheckboxes } from './generic_components/ConditionByCheckboxes';
import { LoadingCondition } from './generic_components/LoadingCondition';
import { useUpdateRule } from './hooks/useUpdateRule';
import { WeightValuesConditions } from './weightValuesConditions/WeightValuesConditions';

interface Props {
  fieldPrefix: string;
  headerText?: string;
  isGlobalSpecial?: boolean;
  isLoading: boolean;
  subHeaderText?: string;
}
export const applyToOptions = [
  {
    id: RULE_OPTIONS[2].value,
    label: 'Category, brand, and more',
    value: RULE_OPTIONS[2].value,
  },
  {
    id: RULE_OPTIONS[1].value,
    label: 'Individual products',
    value: RULE_OPTIONS[1].value,
  },
];
export const ConditionsCard = ({
  fieldPrefix,
  headerText,
  isGlobalSpecial,
  isLoading,
  subHeaderText,
}: Props) => {
  const { setValue, watch, getValues } = useFormContext();
  const {
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useContext(SpecialsModalContext);
  const { isDisabled: globalSpecialDisabled } = useContext(
    GlobalSpecialsModalContext
  );

  const applyToWatch = watch(`${fieldPrefix}.apply_to`);
  const specialTypeWatch = watch('special_type');
  const isProductSpecial = specialTypeWatch === 'product';
  const isBundleSpecial = specialTypeWatch === 'bundle';
  const isBulkSpecial = specialTypeWatch === 'bulk_pricing';
  const isSpendingThresholdSpecial = specialTypeWatch === 'spending_threshold';
  const isCartTotalSpecial = specialTypeWatch === 'cart_total';
  const isGroupSpecial = specialTypeWatch === 'qualified_group';
  const rulesWatch = watch(`${fieldPrefix}.rules`);

  const specialTypeFormatted = useMemo(() => {
    if (!specialTypeWatch || specialTypeWatch === 'select') return null;
    return specialTypeWatch.split('_').join(' ');
  }, [specialTypeWatch]);

  const prices = useMemo(() => {
    return get(rulesWatch, 'product_threshold', {});
  }, [convertRulesToString(rulesWatch)]);

  const onPricesUpdate = (prices: ProductThresholdConditions) => {
    setValue(`${fieldPrefix}.rules.product_threshold`, prices, {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    const newApplyTo = getApplyToSelection(rulesWatch);
    setValue(`${fieldPrefix}.apply_to`, newApplyTo);
  }, [specialTypeWatch]);

  const onCategoriesUpdate = (
    kinds: any,
    includesOrExcludes: 'includes' | 'excludes'
  ) => {
    const rules =
      rulesWatch && rulesWatch[includesOrExcludes]
        ? rulesWatch[includesOrExcludes]
        : null;

    if (isProductSpecial || isCartTotalSpecial) {
      const firstCondition = rules?.[0] ?? {};

      const newRules = {
        ...rulesWatch,
        [includesOrExcludes]: [
          {
            ...firstCondition,
            kinds: kinds,
          },
        ],
      };

      if (isCartTotalSpecial) {
        const cartTotalThresholdValue = getValues(
          'conditions.cart_total.threshold'
        );

        newRules['settings'] = {
          cart_threshold: cartTotalThresholdValue,
        };
      }
      setValue(`${fieldPrefix}.rules`, newRules, {
        shouldDirty: true,
      });
    } else {
      const firstCondition = rules?.[0] ?? {};
      setValue(
        `${fieldPrefix}.rules`,
        {
          ...rulesWatch,
          [includesOrExcludes]: [{ ...firstCondition, kinds }],
        },
        { shouldDirty: true }
      );
    }
  };

  const weightValuesEnabled = useFlag(FLAGS.scWeightValuesFrontend);

  const { data: weights, onUpdate: onWeightsUpdate } = useUpdateRule(
    'weights',
    'includes',
    rulesWatch,
    (newRules: SpecialRulesV2) =>
      setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true })
  );

  const { data: excludedWeights, onUpdate: onExcludeWeightsUpdate } =
    useUpdateRule(
      'weights',
      'excludes',
      rulesWatch,
      (newRules: SpecialRulesV2) =>
        setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true })
    );

  const { data: weightValues, onUpdate: onWeightValuesUpdate } = useUpdateRule(
    'weight_values',
    'includes',
    rulesWatch,
    (newRules: SpecialRulesV2) =>
      setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true })
  );

  const { data: excludedWeightValues, onUpdate: onExcludedWeightValuesUpdate } =
    useUpdateRule(
      'weight_values',
      'excludes',
      rulesWatch,
      (newRules: SpecialRulesV2) =>
        setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true })
    );

  const { data: lineage, onUpdate: onLineageUpdate } = useUpdateRule(
    'lineages',
    'includes',
    rulesWatch,
    (newRules: SpecialRulesV2) =>
      setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true })
  );

  const { data: excludedLineage, onUpdate: onExcludeLineageUpdate } =
    useUpdateRule(
      'lineages',
      'excludes',
      rulesWatch,
      (newRules: SpecialRulesV2) => {
        setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true });
      }
    );

  const { data: bulkWeights, onUpdate: onBulkWeightsUpdate } = useUpdateRule(
    'bulk_weights',
    'includes',
    rulesWatch,
    (newRules: SpecialRulesV2) =>
      setValue(`${fieldPrefix}.rules`, newRules, { shouldDirty: true })
  );

  const isAnyPosSynced = isJanePosSynced || posSynced;
  const isDisabled = isAnyPosSynced || isReadOnly || globalSpecialDisabled;

  const [hasProductExclusion, setHasProductExclusion] = useState(false);
  useEffect(() => {
    const hasExclusion = get(rulesWatch, 'excludes.0.product_ids', []);
    setHasProductExclusion(!!hasExclusion.length);
  }, [rulesWatch]);

  const [hasOtherExclusions, setHasOtherExclusions] = useState(false);

  useEffect(() => {
    const kindExclusion = get(rulesWatch, 'excludes.0.kinds', []);
    const brandExclusion = get(rulesWatch, 'excludes.0.brands', []);
    const lineageExclusion = get(rulesWatch, 'excludes.0.lineages', []);
    setHasOtherExclusions(
      !!kindExclusion.length ||
        !!brandExclusion.length ||
        !!lineageExclusion.length
    );
  }, [rulesWatch]);

  const allowCopyConditions =
    useFlag(FLAGS.cnAllowCopyConditionsOver) &&
    fieldPrefix === 'display.discounted_rules';

  if (isGroupSpecial) {
    return null;
  }

  const copyingConditionsOver =
    allowCopyConditions &&
    watch(
      `display.${specialTypeWatch}.settings.copy_conditions_for_discounted_products`
    );

  // child components that rely on rulesWatch to bootstrap their own state should not be allowed to render when it is undefined.
  // when editing an existing special, there is a moment between when isLoading becomes false after the special is fetched and when
  // the form state is updated and rulesWatch becomes available. If we render components like BrandConditions at that time,
  // they fail to fetch data they need because they rely on getting values from rulesWatch
  const loading = isLoading || !rulesWatch;

  return (
    <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
      <Card.Content>
        <Flex p={24} flexDirection="column">
          <Typography
            variant="header-bold"
            mb={subHeaderText ? 0 : FORM_FIELD_DEFAULT_MARGIN}
          >
            {headerText ? (
              headerText
            ) : (
              <>
                Conditions{' '}
                {!loading &&
                  specialTypeFormatted &&
                  `for ${specialTypeFormatted} special`}
              </>
            )}
          </Typography>
          {subHeaderText && (
            <Typography color={'grays-mid'} mb={FORM_FIELD_DEFAULT_MARGIN}>
              {subHeaderText}
            </Typography>
          )}
          {(isBundleSpecial || isSpendingThresholdSpecial) &&
          fieldPrefix === 'display.required_rules' ? (
            <Flex flexDirection="column" mb={FORM_FIELD_DEFAULT_MARGIN}>
              <SpecialTypeConditionsCriteria rule="required_rules" />
              <Flex flexDirection="row" mb={FORM_FIELD_DEFAULT_MARGIN}>
                <Flex flexDirection="column" width="100%">
                  <Typography variant="body-bold" mb={24}>
                    Discount required products
                  </Typography>
                  {isBundleSpecial ? (
                    <Form.CheckboxField
                      width={'100%'}
                      name={`display.${specialTypeWatch}.settings.allow_discounts_on_required_products`}
                      label={'Also apply discount to required products'}
                      disabled={isDisabled}
                    />
                  ) : (
                    <Form.CheckboxField
                      width={'100%'}
                      name={`display.${specialTypeWatch}.allow_discounts_on_required_products`}
                      label={'Also apply discount to required products'}
                      disabled={isDisabled}
                    />
                  )}
                </Flex>
                {
                  <Flex flexDirection="column" width="100%">
                    <Typography variant="body-bold" mb={24}>
                      Exclude required products from other specials
                    </Typography>
                    {isBundleSpecial ? (
                      <Form.CheckboxField
                        width={'100%'}
                        name={`display.${specialTypeWatch}.settings.exclude_product_specials_from_bundle_qualifiers`}
                        label={
                          'Prevent required products from being discounted by Product Specials'
                        }
                        disabled={isDisabled}
                      />
                    ) : (
                      <Form.CheckboxField
                        width={'100%'}
                        name={`display.${specialTypeWatch}.exclude_product_specials_from_bundle_qualifiers`}
                        label={
                          'Prevent required products from being discounted by Product Specials'
                        }
                        disabled={isDisabled}
                      />
                    )}
                  </Flex>
                }
              </Flex>
            </Flex>
          ) : (
            <Flex flexDirection="column" mb={FORM_FIELD_DEFAULT_MARGIN}>
              <SpecialTypeConditionsCriteria rule="discounted_rules" />{' '}
            </Flex>
          )}
          {allowCopyConditions && isBundleSpecial && (
            <Flex flexDirection="column" mb={FORM_FIELD_DEFAULT_MARGIN}>
              <Form.CheckboxField
                width={'100%'}
                name={`display.${specialTypeWatch}.settings.copy_conditions_for_discounted_products`}
                label={'Use same conditions as required products'}
              />
            </Flex>
          )}
          {allowCopyConditions && isSpendingThresholdSpecial && (
            <Flex flexDirection="column" mb={FORM_FIELD_DEFAULT_MARGIN}>
              <Form.CheckboxField
                width={'100%'}
                name={`display.${specialTypeWatch}.copy_conditions_for_discounted_products`}
                label={'Use same conditions as required products'}
              />
            </Flex>
          )}
          {!copyingConditionsOver && (
            <Flex flexDirection="column">
              <Typography variant="body-bold" mb={FORM_FIELD_DEFAULT_MARGIN}>
                Apply to
              </Typography>
              {loading ? (
                <Skeleton direction="row" animate>
                  <Skeleton.Bone height={BODY_LINE_HEIGHT} width="20%" />
                  <Skeleton.Bone
                    height={BODY_LINE_HEIGHT}
                    ml={12}
                    width="20%"
                  />
                  <Skeleton.Bone
                    height={BODY_LINE_HEIGHT}
                    ml={12}
                    width="20%"
                  />
                </Skeleton>
              ) : (
                <Form.RadioFieldGroup
                  shouldUnregister={false}
                  name={`${fieldPrefix}.apply_to`}
                  row={true}
                  options={applyToOptions}
                  disabled={isDisabled}
                />
              )}
            </Flex>
          )}
          {!copyingConditionsOver && <Modal.ContentDivider />}
          {loading ? (
            <>
              <LoadingCondition />
              <LoadingCondition />
              <LoadingCondition />
            </>
          ) : (
            !copyingConditionsOver && (
              <>
                {applyToWatch === RULE_OPTIONS[2].value && (
                  <>
                    <CategoryConditions
                      fieldPrefix={fieldPrefix}
                      isGlobalSpecial={isGlobalSpecial}
                      type={ConditionType.Categories}
                      onConditionsUpdate={onCategoriesUpdate}
                    />
                    {!(isBulkSpecial || isBundleSpecial) && (
                      <CategoryConditions
                        disableAddButton={hasProductExclusion}
                        fieldPrefix={fieldPrefix}
                        isGlobalSpecial={isGlobalSpecial}
                        type={ConditionType.ExcludeCategories}
                        onConditionsUpdate={onCategoriesUpdate}
                      />
                    )}
                    <BrandConditions
                      fieldPrefix={fieldPrefix}
                      isGlobalSpecial={isGlobalSpecial}
                      type={ConditionType.Brands}
                    />
                    {!(isBulkSpecial || isBundleSpecial) && (
                      <BrandConditions
                        disableAddButton={hasProductExclusion}
                        fieldPrefix={fieldPrefix}
                        isGlobalSpecial={isGlobalSpecial}
                        type={ConditionType.ExcludeBrands}
                      />
                    )}
                    <ConditionByCheckboxes
                      fieldPrefix={fieldPrefix}
                      conditions={lineage}
                      onConditionsUpdate={onLineageUpdate}
                      type={ConditionType.Lineage}
                    />
                    {(isProductSpecial ||
                      isCartTotalSpecial ||
                      isSpendingThresholdSpecial) && (
                      <ConditionByCheckboxes
                        disableAddButton={hasProductExclusion}
                        fieldPrefix={fieldPrefix}
                        conditions={excludedLineage}
                        onConditionsUpdate={onExcludeLineageUpdate}
                        type={ConditionType.ExcludeLineages}
                      />
                    )}
                  </>
                )}
                {applyToWatch === RULE_OPTIONS[1].value && (
                  <ProductsConditions
                    fieldPrefix={fieldPrefix}
                    isGlobalSpecial={isGlobalSpecial}
                    type={ConditionType.Products}
                  />
                )}
                {isBulkSpecial ? (
                  <WeightAndPriceConditions
                    fieldPrefix={fieldPrefix}
                    conditions={bulkWeights}
                    onConditionsUpdate={onBulkWeightsUpdate}
                    type={ConditionType.WeightAndPrice}
                  />
                ) : (
                  <>
                    {!weightValuesEnabled ? (
                      <ConditionByCheckboxes
                        fieldPrefix={fieldPrefix}
                        conditions={weights}
                        onConditionsUpdate={onWeightsUpdate}
                        type={ConditionType.Weight}
                      />
                    ) : (
                      <WeightValuesConditions
                        conditions={weightValues}
                        onConditionsUpdate={onWeightValuesUpdate}
                        type={ConditionType.WeightValues}
                      />
                    )}
                    {(isProductSpecial ||
                      isCartTotalSpecial ||
                      isSpendingThresholdSpecial) &&
                      (!weightValuesEnabled ? (
                        <ConditionByCheckboxes
                          fieldPrefix={fieldPrefix}
                          conditions={excludedWeights}
                          onConditionsUpdate={onExcludeWeightsUpdate}
                          type={ConditionType.ExcludeWeights}
                        />
                      ) : (
                        <WeightValuesConditions
                          conditions={excludedWeightValues}
                          onConditionsUpdate={onExcludedWeightValuesUpdate}
                          type={ConditionType.ExcludeWeightValues}
                        />
                      ))}
                    <PriceConditions
                      fieldPrefix={fieldPrefix}
                      conditions={prices}
                      onConditionsUpdate={onPricesUpdate}
                      type={ConditionType.Price}
                    />
                  </>
                )}
                <ProductsConditions
                  disableAddButton={hasOtherExclusions}
                  fieldPrefix={fieldPrefix}
                  isGlobalSpecial={isGlobalSpecial}
                  type={ConditionType.ExcludeProducts}
                />
              </>
            )
          )}
        </Flex>
      </Card.Content>
    </Card>
  );
};
